import type {
  GalleryItem,
  GalleryItemType,
} from '@freelancer/ui/gallery-types';
import type { VideoFileType } from '../helpers';
import { ImageFileType } from '../helpers';

export const DEFAULT_FILE_THUMBNAIL = 'bits/media-viewer/media-thumbnail.png';
export const DEFAULT_VIDEO_FILE_THUMBNAIL =
  'bits/media-viewer/video-thumbnail.png';
export const DEFAULT_FILE_THUMBNAIL_WIDTH = '192';
export const DEFAULT_FILE_THUMBNAIL_HEIGHT = 152;
export const DEFAULT_FILE_THUMBNAIL_FILE_TYPE = `image/${ImageFileType.PNG}`;

export type MediaViewerMode = 'modal' | 'inline';

export type MediaViewerSlideTransition =
  | 'slide'
  | 'slide-vertical'
  | 'fade'
  | 'zoom-in'
  | 'zoom-out';

export type PictureOptimizationParameters =
  | {
      readonly width?: number;
      readonly height?: number;
      readonly cropToFit: false;
    }
  | {
      readonly width: number;
      readonly height: number;
      readonly cropToFit: true;
    };

export interface MediaPictureItem extends GalleryItem {
  type: GalleryItemType.PICTURE;
  /**
   * Used to specify expected intrinsic image sizes depending different display sizes.
   * Goes in tandem with the `srcset` attribute.
   *
   * Is similar to the `sizes` input of the `fl-picture` component.
   * Used for responsive images and image optimization.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#sizes | MDN - img sizes attribute documentation}
   */
  readonly sizes?: string;
  /**
   * A comma separated list of image sources.
   *
   * Used to specify different image sources for different display sizes and different image sizes.
   * Only one of the srcset URLs will be used, depending on the display size and the `sizes` attribute.
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#srcset | MDN - img srcset attribute document }
   * @see {@link https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images | MDN - Responsive images documentation}
   *
   * @example
   * 'http://localhost:7766/assets/story/gallery-1.jpg?&width=640 640w'
   *
   * 'https://www.f-staging-cdn.com/assets/bits/en/assets/story/gallery-6.png?&width=600 600w, https://www.f-staging-cdn.com/assets/bits/en/assets/story/gallery-6.png?&width=1200 1200w'
   *
   * 'https://www.f-cdn.com/assets/main/en/assets/home/crowd-favorites/ai-generated-art.jpg?&width=600 1x, https://www.f-cdn.com/assets/main/en/assets/home/crowd-favorites/ai-generated-art.jpg?&width=1200 2x'
   */
  readonly srcset?: string;
  readonly fileType?: ImageFileType;
  readonly thumbnailOptimizationParameters?: PictureOptimizationParameters;
}

export interface MediaVideoItem extends GalleryItem {
  type: GalleryItemType.VIDEO;
  readonly fileType?: VideoFileType;
  /** Initial image displayed on the video before it is played by the user */
  readonly posterSrc?: string;
}

export interface MediaFileItem extends GalleryItem {
  type: GalleryItemType.FILE;
}

export interface PDFFileItem extends GalleryItem {
  type: GalleryItemType.PDF;
}

export type MediaItem =
  | MediaFileItem
  | MediaVideoItem
  | MediaPictureItem
  | PDFFileItem;
